import { Instance, SnapshotIn, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import { deleteCgSchool, getCgSchool, ICreateCgSchool, updateCgSchool } from '@common/api/cgSchools';
import CgCity from '@common/models/CgCities/CgCity';
import User from '@common/models/Users/User';
import { ICgSchoolsModel } from './CgSchools';

const CgSchoolCoordinatorUser = types
	.compose(
		User,
		types.model({
			middle_name: types.maybeNull(types.string),
			email_verified_at: types.maybeNull(types.string),
			referrer_id: types.maybeNull(types.number),
			referral_code: types.maybeNull(types.string),
			updated_at: types.maybeNull(types.string),
			deleted_at: types.maybeNull(types.string),
			lang: types.maybeNull(types.string),
		}),
	)
	.preProcessSnapshot((snapshot: any) => ({
		user_id: null,
		avatar_url: null,
		avatar: null,
		...snapshot,
	}));

const CgSchoolCoordinator = types.model('CgSchoolCoordinator', {
	id: types.identifierNumber,
	user_id: types.maybeNull(types.number),
	c_g_school_id: types.maybeNull(types.number),
	phone: types.maybeNull(types.string),
	created_at: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
	deleted_at: types.maybeNull(types.string),
	user: types.maybeNull(CgSchoolCoordinatorUser),
});

const CgSchool = types
	.model('CgSchool', {
		id: types.maybe(types.number),
		name: types.optional(types.string, ''),
		city: types.maybeNull(CgCity),
		coordinators: types.optional(types.array(CgSchoolCoordinator), []),
		custom_city_name: types.maybeNull(types.string),

	})
	.volatile(() => ({
		loading: false,
	}))
	.actions((self) => ({
		fetch: flow(function* (id: number) {
			self.loading = true;
			try {
				const result = yield getCgSchool({ id: id || self.id });
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield deleteCgSchool({ id: self.id });
				const parent = getParent<ICgSchoolsModel>(self, 2);
				parent.removeItem(self as ICgSchoolModel);
			} catch (e) {
				self.loading = false;
			}
		}),
		save: flow(function* (data: ICreateCgSchool) {
			self.loading = true;
			try {
				const result = yield updateCgSchool({
					id: self.id,
					payload: data,
				});

				applySnapshot(self, result);
			} catch (e) {
				throw e?.response?.data ?? e;
			} finally {
				self.loading = false;
			}
		}),

	}));

export default CgSchool;

export interface ICgSchoolModel extends Instance<typeof CgSchool> { }
export type ICgSchoolSnapshotIn = SnapshotIn<typeof CgSchool>;
